import {FC, useEffect, useState} from 'react'
import {api, ApiError, formatEvery} from '../../services/api'
import {useToast} from '../../components/Toast'
import {Spinner} from '../../components/Spinner'
import {confirmDialog} from '../../components/dialog'

const ACCENT = '#039540'
const INK = '#1b1b29'

/** On-demand bed wash for renters with an active stay. Used on Home + Payments.
 *  Fee + auto-wash interval come straight from Payment Settings. */
const BedWashCard: FC<{cardOnFile?: boolean; bookingId?: string | null; fill?: boolean}> = ({cardOnFile = true, bookingId = null, fill = false}) => {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [fee, setFee] = useState('5.00')
  const [washEvery, setWashEvery] = useState('every 6 weeks')
  const [nextWashAt, setNextWashAt] = useState<string | null>(null)

  useEffect(() => {
    api
      .paymentMethods()
      .then((m) => {
        setFee(Number(m.bedWashFee).toFixed(2))
        setWashEvery(formatEvery(m.bedWashIntervalValue, m.bedWashIntervalUnit))
      })
      .catch(() => {})
    api
      .bedWashNext()
      .then((n) => setNextWashAt(n.autoEnabled ? n.nextWashAt : null))
      .catch(() => {})
  }, [])

  const request = async () => {
    if (!cardOnFile) {
      const save = await confirmDialog({
        title: 'No card on file',
        text: 'Save a card first, then you can request a bed wash.',
        confirmText: 'Save card',
      })
      if (!save) return
      try {
        const {url} = await api.saveCardSession(bookingId ?? undefined)
        window.location.href = url
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : 'Could not open the card form')
      }
      return
    }
    const ok = await confirmDialog({
      title: 'Request a bed wash?',
      text: `We'll charge $${fee} to your saved card and housekeeping will take care of it.`,
      confirmText: `Pay $${fee}`,
    })
    if (!ok) return
    setBusy(true)
    try {
      // Log the request first so its id links the wash job — the cleaner's
      // Start/Done then flows back to this request's status.
      const req = await api.logBedWashRequest(fee)
      const res = await api.startBedWash(req.id)
      if ('url' in res) {
        window.location.href = res.url // no saved card yet → pay + save one via Checkout
        return
      }
      // Charged the card saved at the deposit — no redirect.
      toast.success(`Bed wash requested — $${fee} charged to your saved card.`)
      setTimeout(() => window.location.reload(), 1200)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not start the bed-wash payment')
      setBusy(false)
    }
  }
  return (
    <div className='ccp-fade-up-2' style={{background: 'linear-gradient(160deg, #f1faf4, #ffffff)', border: '1px solid #e6f3ea', borderRadius: 16, padding: '24px 26px', marginBottom: fill ? 0 : 24, height: fill ? '100%' : undefined, boxSizing: 'border-box'}}>
      <h2 style={{fontSize: 17, fontWeight: 800, color: INK, margin: '0 0 8px'}}>Request a bed wash · ${fee}</h2>
      <p style={{fontSize: 13.5, color: INK, lineHeight: 1.55, margin: '0 0 12px'}}>
        Fresh sheets on demand — <strong>${fee}</strong> charged to your card and housekeeping takes care of it. For hygiene, every bed is washed at least once <strong>{washEvery}</strong>; if you haven’t requested one in that window, we’ll wash it and charge the ${fee} automatically.
      </p>
      {nextWashAt && (
        <div style={{fontSize: 13, color: INK, background: 'rgba(3,149,64,0.08)', border: '1px solid #cdeadd', borderRadius: 10, padding: '10px 12px', margin: '0 0 16px'}}>
          🧺 Next automatic sheet change &amp; ${fee} charge: <strong>{new Date(nextWashAt).toLocaleDateString()}</strong>
        </div>
      )}
      <button onClick={request} disabled={busy} style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 22px', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1}}>
        {busy ? <Spinner size={14} /> : `Request bed wash · $${fee}`}
      </button>
    </div>
  )
}

export {BedWashCard}
